import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useMasterData } from '../contexts/MasterDataContext';
import { mergeConsecutiveBookings, detectDuplicates } from '../utils/bookingUtils';
import { istToday, istDateOffset } from '../utils/bookingFormatters';
import { BookingsFilterBar } from '../components/bookings/BookingsFilterBar';
import { BulkSettlementBar } from '../components/bookings/BulkSettlementBar';
import { BookingsTable } from '../components/bookings/BookingsTable';
import { EditBookingDialog } from '../components/bookings/EditBookingDialog';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { API } from '../utils/apiConfig';

export default function BookingsListPage() {
  const { getVenueNames, getGamesForVenue, getCourtsForVenueGame, allGames } = useMasterData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [showMerged, setShowMerged] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [availableCourts, setAvailableCourts] = useState([]);
  const [settleOpen, setSettleOpen] = useState(false);
  const [settling, setSettling] = useState(false);

  const VENUES = getVenueNames();
  const ALL_GAMES = allGames.length > 0 ? allGames : ['Pickleball', 'Badminton', 'Cricket', 'Football Half', 'Football Full'];

  const [filters, setFilters] = useState({
    venue: '', game: '', court: '', date: '', dateFrom: '', dateTo: '', payment_mode: '', booking_source: '',
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [editingBooking, setEditingBooking] = useState(null);
  const [deletingBooking, setDeletingBooking] = useState(null);
  const [cancellingBooking, setCancellingBooking] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, [dateFilter, filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    applyFilters();
  }, [bookings, searchTerm, filters, dateFilter, showMerged, bulkMode, sortConfig]);

  // Deep-link: /bookings?edit=<id> auto-opens that booking's edit dialog
  // (used by conflict "Edit this booking" links from /email-bookings & edit forms).
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || bookings.length === 0) return;
    const target = bookings.find((b) => b.id === editId);
    if (target) {
      setEditingBooking(target);
    } else {
      toast.error('Booking not found for the requested edit link');
    }
    searchParams.delete('edit');
    setSearchParams(searchParams, { replace: true });
  }, [bookings, searchParams]);

  const SORT_ACCESSORS = {
    player_name: (b) => (b.player_name || '').toLowerCase(),
    player_mobile: (b) => b.player_mobile || '',
    venue: (b) => (b.venue || '').toLowerCase(),
    game: (b) => (b.game || '').toLowerCase(),
    court: (b) => (b.court || '').toLowerCase(),
    date: (b) => b.date || '',
    start_time: (b) => b.start_time || '',
    amount: (b) => parseFloat(b.amount) || 0,
    pending: (b) => Math.max(0, (b.amount || 0) - (b.advance_payment || 0)),
    payment_mode: (b) => (b.payment_mode || '').toLowerCase(),
    booking_source: (b) => (b.booking_source || '').toLowerCase(),
    created_at: (b) => b.created_at || '',
    booking_id: (b) => b.booking_id || '',
  };

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Underlying bookings (expand merged rows) in the current filtered view with Pending > 0
  const pendingSettleItems = (() => {
    const out = [];
    const pendingOf = (b) => Math.max(0, (parseFloat(b.amount) || 0) - (parseFloat(b.advance_payment) || 0));
    filteredBookings.forEach((row) => {
      const underlying = Array.isArray(row.original_bookings) && row.original_bookings.length > 0
        ? row.original_bookings
        : [row];
      underlying.forEach((b) => {
        if (b.id && pendingOf(b) > 0) out.push({ id: b.id, pending: pendingOf(b) });
      });
    });
    const seen = new Set();
    return out.filter((x) => (seen.has(x.id) ? false : seen.add(x.id)));
  })();
  const pendingCount = pendingSettleItems.length;
  const pendingTotal = pendingSettleItems.reduce((s, x) => s + x.pending, 0);

  const handleBulkSettle = async () => {
    setSettling(true);
    try {
      const res = await axios.post(`${API}/bookings/bulk-settle`, {
        booking_ids: pendingSettleItems.map((x) => x.id),
      });
      toast.success(`Settled ${res.data.settled} booking(s) · ₹${res.data.amount_collected} collected`);
      setSettleOpen(false);
      fetchBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to settle payments');
    } finally {
      setSettling(false);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      // Compute server-side date range based on the current dateFilter
      // to avoid fetching the entire booking history on every page load.
      const today = istToday();
      const params = {};

      if (dateFilter === 'today') {
        params.date = today;
      } else if (dateFilter === 'yesterday') {
        params.date = istDateOffset(-1);
      } else if (dateFilter === 'tomorrow') {
        params.date = istDateOffset(1);
      } else if (dateFilter === 'this_week') {
        const d = new Date(`${today}T00:00:00`);
        const dayOfWeek = d.getDay();
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        params.date_from = weekStart.toLocaleDateString('en-CA');
        params.date_to   = weekEnd.toLocaleDateString('en-CA');
      } else if (dateFilter === 'this_month') {
        const [yr, mo] = today.split('-');
        const lastDay = new Date(parseInt(yr), parseInt(mo), 0).getDate();
        params.date_from = `${yr}-${mo}-01`;
        params.date_to   = `${yr}-${mo}-${String(lastDay).padStart(2, '0')}`;
      } else if (dateFilter === 'this_year') {
        const yr = today.slice(0, 4);
        params.date_from = `${yr}-01-01`;
        params.date_to   = `${yr}-12-31`;
      } else if (dateFilter === 'custom') {
        if (filters.dateFrom) params.date_from = filters.dateFrom;
        if (filters.dateTo)   params.date_to   = filters.dateTo;
      }
      // dateFilter === 'all' → no date restriction

      const response = await axios.get(`${API}/bookings`, { params });
      const sortedBookings = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setBookings(sortedBookings);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...bookings];
    const today = istToday();

    // Bulk toggle: ON → only Bulk Book entries; OFF (default) → exclude Bulk Book
    if (bulkMode) {
      result = result.filter((b) => b.booking_source === 'Bulk Book');
    } else {
      result = result.filter((b) => b.booking_source !== 'Bulk Book');
    }

    result = detectDuplicates(result);

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (b) =>
          b.player_name.toLowerCase().includes(term) ||
          b.player_mobile.includes(searchTerm) ||
          b.booking_id.toLowerCase().includes(term) ||
          b.game.toLowerCase().includes(term)
      );
    }

    // Date filter: server already scoped the data to the right window.
    // For 'custom' without date range, show today as fallback.
    if (dateFilter === 'custom' && !filters.dateFrom && !filters.dateTo) {
      result = result.filter((b) => b.date === today);
    }

    if (filters.venue) {
      result = result.filter((b) => b.venue === filters.venue);
    }
    if (filters.game) {
      result = result.filter((b) => b.game === filters.game);
    }
    // Court options reflect the current date/venue/game view so a selected court always has rows
    setAvailableCourts(Array.from(new Set(result.map((b) => b.court).filter(Boolean))).sort());
    if (filters.court) {
      result = result.filter((b) => b.court === filters.court);
    }
    if (filters.payment_mode) {
      result = result.filter((b) => b.payment_mode === filters.payment_mode);
    }
    if (filters.booking_source) {
      result = result.filter((b) => b.booking_source === filters.booking_source);
    }

    // Apply merging if enabled (duplicates are already marked and shown separately)
    if (showMerged) {
      const nonDuplicates = result.filter((b) => !b.is_duplicate);
      const duplicates = result.filter((b) => b.is_duplicate);
      result = [...mergeConsecutiveBookings(nonDuplicates, false), ...duplicates];
    }

    // Apply column sorting (overrides default date-desc when a column is selected)
    if (sortConfig.key && SORT_ACCESSORS[sortConfig.key]) {
      const acc = SORT_ACCESSORS[sortConfig.key];
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      result = [...result].sort((a, b) => {
        const av = acc(a);
        const bv = acc(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }

    setFilteredBookings(result);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/bookings/${deletingBooking.id}`);
      toast.success('Booking deleted successfully');
      setDeletingBooking(null);
      fetchBookings();
    } catch (error) {
      console.error('Failed to delete booking:', error);
      toast.error('Failed to delete booking');
    }
  };

  const handleCancel = async () => {
    try {
      await axios.post(`${API}/bookings/${cancellingBooking.id}/cancel`, {});
      toast.success('Booking cancelled — slot is now free for re-booking');
      setCancellingBooking(null);
      fetchBookings();
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      toast.error('Failed to cancel booking');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const totalAmount = filteredBookings.filter((b) => !b.is_duplicate).reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="bookings-list-page">
        <div className="text-center">
          <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">Bookings</h1>
          <p className="text-slate-600 mt-1" data-testid="bookings-count-amount">
            {filteredBookings.length} booking{filteredBookings.length !== 1 ? 's' : ''} found
            {' · '}
            <span className="font-semibold text-slate-900">
              {'\u20B9'}{totalAmount.toLocaleString('en-IN')}
            </span>
            {' total'}
          </p>
        </div>
        <div className="flex justify-center gap-3">
          <Button onClick={fetchBookings} disabled={loading} variant="outline" data-testid="refresh-bookings-button">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => navigate('/office/bookings/new')} className="bg-primary hover:bg-primary-hover transition-colors duration-200" data-testid="new-booking-button">
            + New Booking
          </Button>
        </div>

        <BookingsFilterBar
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          dateFilter={dateFilter} setDateFilter={setDateFilter}
          filters={filters} setFilters={setFilters}
          venues={VENUES} allGames={ALL_GAMES} courtOptions={availableCourts}
          bulkMode={bulkMode} setBulkMode={setBulkMode}
          showMerged={showMerged} setShowMerged={setShowMerged}
        />

        <BulkSettlementBar
          pendingCount={pendingCount} pendingTotal={pendingTotal}
          settleOpen={settleOpen} setSettleOpen={setSettleOpen}
          settling={settling} onSettle={handleBulkSettle}
        />

        <BookingsTable
          bookings={filteredBookings}
          showMerged={showMerged}
          sortConfig={sortConfig}
          onSort={requestSort}
          onEdit={setEditingBooking}
          onCancel={setCancellingBooking}
          onDelete={setDeletingBooking}
        />
      </div>

      <AlertDialog open={!!deletingBooking} onOpenChange={() => setDeletingBooking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this booking? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!cancellingBooking} onOpenChange={() => setCancellingBooking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Booking</AlertDialogTitle>
            <AlertDialogDescription>
              {cancellingBooking && (
                <>
                  Cancel <strong>{cancellingBooking.player_name}</strong>'s booking on{' '}
                  <strong>{cancellingBooking.court}</strong> ({cancellingBooking.start_time} - {cancellingBooking.end_time})?
                  The slot will be freed for re-booking and this will be logged under Cancellations with your name.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-cancel">Keep Booking</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-cancel">
              Cancel Booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editingBooking && (
        <EditBookingDialog
          booking={editingBooking}
          onClose={() => setEditingBooking(null)}
          onSuccess={() => {
            setEditingBooking(null);
            fetchBookings();
          }}
          getVenueNames={getVenueNames}
          getGamesForVenue={getGamesForVenue}
          getCourtsForVenueGame={getCourtsForVenueGame}
        />
      )}
    </DashboardLayout>
  );
}
